import {AsyncInlineError, inlineError} from '@s-ui/decorators'
import {Repository} from '@s-ui/domain'
import {toQueryString} from '@s-ui/js/lib/string'

import FetcherFactory from '../../fetcher/Fetchers/factory.js'
import {FetchOrFail} from '../../fetcher/Fetchers/FetchOrFail/index.js'
import PageValueObjectsFactory from '../../page/ValueObjects/factory.js'
import UserServicesFactory from '../../user/Services/factory.js'
import {TransactionsEntityFactory} from '../Entities/factory.js'
import {ReservationEntity} from '../Entities/ReservationEntity.js'
import {TradeEntity} from '../Entities/TradeEntity.js'
import {AlreadyCancelledTradeTransactionsError} from '../Errors/AlreadyCancelledTradeTransactionsError.js'
import {TransactionErrorsFactory} from '../Errors/factory.js'
import {TransactionError} from '../Errors/TransactionError.js'
import {UpdateReservationPhoneNumberBadRequestTransactionsError} from '../Errors/UpdateReservationPhoneNumberBadRequestTransactionsError.js'
import {UpdateReservationPhoneNumberNotRequiredTransactionsError} from '../Errors/UpdateReservationPhoneNumberNotRequiredTransactionsError.js'
import {UpdateReservationPhoneNumberReservationNotFoundTransactionsError} from '../Errors/UpdateReservationPhoneNumberReservationNotFoundTransactionsError.js'
import {UpdateReservationPhoneNumberUnexpectedTransactionsError} from '../Errors/UpdateReservationPhoneNumberUnexpectedTransactionsError.js'
import {AdReservationStatusTransactionsValueObject} from '../ValueObjects/AdReservationStatusTransactionsValueObject.js'
import {BuyerTradeDataTransactionsValueObject} from '../ValueObjects/BuyerTradeDataTransactionsValueObject.js'
import {TransactionsValueObjectsFactory} from '../ValueObjects/factory.js'
import {PaymentInitiatedTransactionsValueObject} from '../ValueObjects/PaymentInitiatedTransactionsValueObject.js'
import {PaymentUrlTransactionsValueObject} from '../ValueObjects/PaymentUrlTransactionsValueObject.js'
import {ProfessionalActivitiesTransactionsValueObject} from '../ValueObjects/ProfessionalActivitiesTransactionsValueObject.js'
import {ReservationClientSecretTransactionsValueObject} from '../ValueObjects/ReservationClientSecretTransactionsValueObject.js'
import {ReservationTransactionsValueObject} from '../ValueObjects/ReservationTransactionsValueObject.js'
import {SellerTradeDataTransactionsValueObject} from '../ValueObjects/SellerTradeDataTransactionsValueObject.js'
import {TradeCostsListTransactionsValueObject} from '../ValueObjects/TradeCostsListTransactionsValueObject.js'

/**
 * @class
 * @implements UserRepository
 */
export class HTTPTransactionRepository extends Repository {
  constructor({
    buyerDataErrorsFactory,
    config,
    confirmAgreedPriceTransactionsErrorFactory,
    createTradeTransactionsErrorFactory,
    fetchOrFail,
    fromRawDataToLoanCalculationEntityObjectTransactionsMapper,
    getBuyerTradeDataTransactionsErrorFactory,
    getCurrentUserService,
    getProfessionalActivitiesTransactionsErrorFactory,
    getSellerTradeDataTransactionsErrorFactory,
    getTradeDataTransactionsErrorFactory,
    getTradeCostsListTransactionsErrorFactory,
    httpAuthenticationFetcher,
    informAgreedPriceTransactionsErrorFactory,
    loanB2CEntityFactory,
    loanCalculationsTransactionsValueObjectFactory,
    reservationEntity,
    transactionEntity,
    transactionErrorsFactory,
    transactionNoContentErrorFactory,
    updateBuyerTradeDataTransactionsErrorFactory,
    updateSellerTradeDataTransactionsErrorFactory,
    uploadBuyerIdTransactionsErrorFactory,
    uploadSellerIdTransactionsErrorFactory,
    urlValueObjectFactory
  }) {
    super({config})

    this._config = config
    this._buyerDataErrorsFactory = buyerDataErrorsFactory
    this._confirmAgreedPriceTransactionsErrorFactory = confirmAgreedPriceTransactionsErrorFactory
    this._fetchOrFail = fetchOrFail
    this._getBuyerTradeDataTransactionsErrorFactory = getBuyerTradeDataTransactionsErrorFactory
    this._getCurrentUserService = getCurrentUserService
    this._getProfessionalActivitiesTransactionsErrorFactory = getProfessionalActivitiesTransactionsErrorFactory
    this._getSellerTradeDataTransactionsErrorFactory = getSellerTradeDataTransactionsErrorFactory
    this._fromRawDataToLoanCalculationEntityObjectTransactionsMapper =
      fromRawDataToLoanCalculationEntityObjectTransactionsMapper
    this._createTradeTransactionsErrorFactory = createTradeTransactionsErrorFactory
    this._getTradeDataTransactionsErrorFactory = getTradeDataTransactionsErrorFactory
    this._getTradeCostsListTransactionsErrorFactory = getTradeCostsListTransactionsErrorFactory
    this._httpAuthenticationFetcher = httpAuthenticationFetcher
    this._informAgreedPriceTransactionsErrorFactory = informAgreedPriceTransactionsErrorFactory
    this._loanB2CEntityFactory = loanB2CEntityFactory
    this._loanCalculationsTransactionsValueObjectFactory = loanCalculationsTransactionsValueObjectFactory
    this._reservationEntity = reservationEntity
    this._transactionEntity = transactionEntity
    this._transactionErrorsFactory = transactionErrorsFactory
    this._transactionNoContentErrorFactory = transactionNoContentErrorFactory
    this._updateBuyerTradeDataTransactionsErrorFactory = updateBuyerTradeDataTransactionsErrorFactory
    this._uploadBuyerIdTransactionsErrorFactory = uploadBuyerIdTransactionsErrorFactory
    this._updateSellerTradeDataTransactionsErrorFactory = updateSellerTradeDataTransactionsErrorFactory
    this._uploadSellerIdTransactionsErrorFactory = uploadSellerIdTransactionsErrorFactory
    this._urlValueObjectFactory = urlValueObjectFactory
  }

  static create({config}) {
    return new HTTPTransactionRepository({
      buyerDataErrorsFactory: TransactionErrorsFactory.buyerDataError,
      calculatorPluginTransactionsValueObjectFactory:
        TransactionsValueObjectsFactory.calculatorPluginTransactionsValueObject,
      config,
      confirmAgreedPriceTransactionsErrorFactory: TransactionErrorsFactory.confirmAgreedPriceTransactionsError,
      createTradeTransactionsErrorFactory: TransactionErrorsFactory.createTradeTransactionsError,
      fetchOrFail: FetchOrFail.create({
        config
      }),
      httpAuthenticationFetcher: FetcherFactory.httpAuthenticationFetcher({config}),
      getBuyerTradeDataTransactionsErrorFactory: TransactionErrorsFactory.getBuyerTradeDataTransactionsError,
      getTradeCostsListTransactionsError: TransactionErrorsFactory.getTradeCostsListTransactionsError,
      getSellerTradeDataTransactionsErrorFactory: TransactionErrorsFactory.getSellerTradeDataTransactionsError,
      getCurrentUserService: UserServicesFactory.getCurrentUserService({
        config
      }),
      getProfessionalActivitiesTransactionsErrorFactory:
        TransactionErrorsFactory.getProfessionalActivitiesTransactionsError,
      getTradeCostsListTransactionsErrorFactory: TransactionErrorsFactory.getTradeCostsListTransactionsError,
      getTradeDataTransactionsErrorFactory: TransactionErrorsFactory.getTradeDataTransactionsError,
      informAgreedPriceTransactionsErrorFactory: TransactionErrorsFactory.informAgreedPriceTransactionsError,
      loanB2CEntityFactory: TransactionsEntityFactory.loanB2CEntity,
      loanCalculationsB2CTransactionsEntity: TransactionsEntityFactory.loanCalculationsB2CTransactionsEntity,
      loanCalculationsTransactionsValueObjectFactory:
        TransactionsValueObjectsFactory.loanCalculationsTransactionsValueObject,
      reservationEntity: TransactionsEntityFactory.reservationEntity,
      transactionEntity: TransactionsEntityFactory.transactionEntity,
      transactionErrorsFactory: TransactionErrorsFactory.transactionError,
      transactionNoContentErrorFactory: TransactionErrorsFactory.transactionNoContentError,
      updateBuyerTradeDataTransactionsErrorFactory: TransactionErrorsFactory.updateBuyerTradeDataTransactionsError,
      updateSellerTradeDataTransactionsErrorFactory: TransactionErrorsFactory.updateSellerTradeDataTransactionsError,
      uploadBuyerIdTransactionsErrorFactory: TransactionErrorsFactory.uploadBuyerIdTransactionsError,
      uploadSellerIdTransactionsErrorFactory: TransactionErrorsFactory.uploadSellerIdTransactionsError,
      urlValueObjectFactory: PageValueObjectsFactory.urlValueObject
    })
  }

  @inlineError
  async acceptPurchaseTerms({userEntity}) {
    const userId = userEntity.getId()
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => TransactionError.create('Transaction failed')
      })
      .post(`${this._config.get('API_URL')}/trade-service/accept-terms`, {
        userId
      })

    if (error) return Promise.reject(error)
  }

  @inlineError
  async confirmAgreedPrice(tradeEntity) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._confirmAgreedPriceTransactionsErrorFactory('Transaction failed')
      })
      .put(`${this._config.get('API_URL')}/trades/${tradeEntity.getId()}/agreed-price/accept`, {
        agreedPrice: tradeEntity.getAgreedPrice()
      })

    if (error) return Promise.reject(error)
  }

  @inlineError
  async createTrade({createTradeRequest}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {CONFLICT} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Transaction failed'),
        [CONFLICT]: () => this._createTradeTransactionsErrorFactory('Trade already initiated')
      })
      .post(`${this._config.get('API_URL')}/trades`, createTradeRequest.toJSON())

    if (error) return Promise.reject(error)
  }

  @inlineError
  async getTradeData(tradeEntity) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getTradeDataTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .get(`${this._config.get('API_URL')}/trades/${tradeEntity.getId()}`)

    if (error) return Promise.reject(error)

    const tradeData = response.data

    const {agreedPrice, buyerId, buyerName, sellerId, sellerName} = tradeData

    return TradeEntity.create({agreedPrice, id: tradeEntity.getId(), buyerId, buyerName, sellerId, sellerName})
  }

  @inlineError
  async getBuyerTradeData(tradeEntity) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getBuyerTradeDataTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .get(`${this._config.get('API_URL')}/trades/${tradeEntity.getId()}/buyer`)

    if (error) return Promise.reject(error)

    const buyerTradeData = response.data

    const {address, city, email, lastName, name, personalId, phone, professionalActivityCode, zipCode} = buyerTradeData

    return BuyerTradeDataTransactionsValueObject.create({
      address,
      city,
      email,
      lastName,
      name,
      personalId,
      phone,
      professionalActivityCode,
      zipCode
    })
  }

  @inlineError
  async getSellerTradeData(tradeEntity) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getSellerTradeDataTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .get(`${this._config.get('API_URL')}/trades/${tradeEntity.getId()}/seller`)

    if (error) return Promise.reject(error)

    const sellerTradeData = response.data

    const {address, bankAccount, city, email, lastName, name, personalId, phone, zipCode} = sellerTradeData

    return SellerTradeDataTransactionsValueObject.create({
      address,
      bankAccount,
      city,
      email,
      lastName,
      name,
      personalId,
      phone,
      zipCode
    })
  }

  @inlineError
  async getTradeCostsList(tradeEntity) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getTradeCostsListTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .get(`${this._config.get('API_URL')}/trades/${tradeEntity.getId()}/costs`)

    if (error) return Promise.reject(error)

    const {costs, purchasePrice, totalAmount} = response.data

    return TradeCostsListTransactionsValueObject.fromApiResponse({
      costs,
      purchasePrice,
      totalAmount
    })
  }

  @AsyncInlineError()
  async createReservation(createReservationRequestValueObject) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Create Reservation failed')
      })
      .post(`${this._config.get('API_URL')}/v3/reservations`, createReservationRequestValueObject.toJSON())

    if (error) return Promise.reject(error)

    const {reservationId} = response.data

    return ReservationEntity.createFromApiCreateResponse({id: reservationId})
  }

  @AsyncInlineError()
  async initReservationPayment(initReservationPaymentRequest) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Reservation Payment failed')
      })
      .post(
        `${this._config.get(
          'API_URL'
        )}/reservations/${initReservationPaymentRequest.getReservationId()}/create-payment`,
        {includeTermsAndConditions: initReservationPaymentRequest.getIncludeTermsAndConditions()}
      )

    if (error) return Promise.reject(error)

    const {clientSecret} = response.data

    return ReservationClientSecretTransactionsValueObject.create({clientSecret})
  }

  @inlineError
  async uploadBuyerId(uploadBuyerIdRequestTransactionsValueObject) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const {tradeId, ...requestBody} = uploadBuyerIdRequestTransactionsValueObject.toJSON()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._uploadBuyerIdTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .put(`${this._config.get('API_URL')}/trades/${tradeId}/buyer-id`, requestBody)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async updateBuyerTradeData(updateBuyerTradeDataRequestValueObject) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const {tradeId, ...requestBody} = updateBuyerTradeDataRequestValueObject.toJSON()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._updateBuyerTradeDataTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .put(`${this._config.get('API_URL')}/trades/${tradeId}/buyer`, requestBody)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async updateSellerTradeData(updateSellerTradeDataRequestValueObject) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const {tradeId, ...requestBody} = updateSellerTradeDataRequestValueObject.toJSON()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._updateSellerTradeDataTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .put(`${this._config.get('API_URL')}/trades/${tradeId}/seller`, requestBody)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async uploadSellerId(uploadSellerIdRequestTransactionsValueObject) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const {tradeId, ...requestBody} = uploadSellerIdRequestTransactionsValueObject.toJSON()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._uploadBuyerIdTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .put(`${this._config.get('API_URL')}/trades/${tradeId}/seller-id`, requestBody)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async getProfessionalActivities() {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getProfessionalActivitiesTransactionsErrorFactory('Transaction failed')
      })
      .get(`${this._config.get('API_URL')}/transaction/professional-activities`)

    if (error) return Promise.reject(error)

    const professionalActivitiesRawData = response.data || []

    return ProfessionalActivitiesTransactionsValueObject.fromApiResponse(professionalActivitiesRawData)
  }

  @inlineError
  async informAgreedPrice(informAgreedPriceTradeRequestValueObject) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._informAgreedPriceTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .put(
        `${this._config.get('API_URL')}/trades/${informAgreedPriceTradeRequestValueObject.tradeId}/agreed-price`,
        informAgreedPriceTradeRequestValueObject.toJSONBodyRequest()
      )

    if (error) return Promise.reject(error)
  }

  @inlineError
  async initPayment(initPaymentRequest) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const tradeId = initPaymentRequest.getTradeId()
    const passcode = initPaymentRequest.getPasscode()

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => this._transactionErrorsFactory(`Transaction failed ${JSON.stringify(e)}`),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .post(`${this._config.get('API_URL')}/trades/${tradeId}/initiate-payment`, {passcode})

    if (error) return Promise.reject(error)

    const {paymentUrl} = response.data

    return PaymentUrlTransactionsValueObject.create({paymentUrl})
  }

  @inlineError
  async getPaymentInitiated({tradeId}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => this._transactionErrorsFactory(`Transaction failed ${JSON.stringify(e)}`),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .get(`${this._config.get('API_URL')}/trades/${tradeId.value}/is-payment-initiated`)

    if (error) return Promise.reject(error)

    const {isPaymentInitiated} = response.data

    return PaymentInitiatedTransactionsValueObject.create({isPaymentInitiated})
  }

  @inlineError
  async initTrade({tradeEntity}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {UNPROCESSABLE_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES

    const tradeId = tradeEntity.getId()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => this._transactionErrorsFactory(`Transaction failed ${JSON.stringify(e)}`),
        [UNPROCESSABLE_CONTENT]: () => this._createTradeTransactionsErrorFactory('Trade already initiated')
      })
      .put(`${this._config.get('API_URL')}/trades/${tradeId}/initiate`)

    if (error) return Promise.reject(error)
  }

  @AsyncInlineError()
  async refundReservation({reservationIdVO, refundRequestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const reservationId = reservationIdVO.value

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Refund Reservation failed')
      })
      .put(`${this._config.get('API_URL')}/reservations/${reservationId}/refund`, refundRequestVO.value)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async getLoanCalculations({getLoanCalculationsTransactionsRequest}) {
    const {entry, lender, plateRegistrationDate, price, terms} = getLoanCalculationsTransactionsRequest.toJSON()
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Transaction failed')
      })
      .post(`${this._config.get('API_URL')}/financing/calculator`, {
        entry,
        lender,
        plateRegistrationDate,
        price,
        terms
      })

    if (error) return Promise.reject(error)

    const {
      minEntry,
      maxEntry,
      availableTerms,
      fee,
      capital,
      openingExpenses,
      isOpeningExpensesFinanced,
      tin,
      tae,
      totalAmountDue,
      totalLoanAmount,
      openingPercentage,
      totalInterest,
      totalLoanCost
    } = response.data

    return this._loanCalculationsTransactionsValueObjectFactory({
      minEntry,
      maxEntry,
      availableTerms,
      fee,
      capital,
      openingExpenses,
      isOpeningExpensesFinanced,
      tin,
      tae,
      totalAmountDue,
      totalLoanAmount,
      openingPercentage,
      totalInterest,
      totalLoanCost
    })
  }

  @inlineError
  async getLoanUrl({getLoanUrlTransactionsRequest}) {
    const {
      adId,
      lender,
      price,
      plateRegistrationDate,
      make,
      model,
      version,
      mileage,
      entry,
      minEntry,
      maxEntry,
      minTerms,
      maxTerms,
      terms,
      openingExpenses,
      openingPercentage,
      isOpeningExpensesFinanced,
      totalInterest,
      totalLoanCost,
      totalLoanAmount,
      totalAmountDue,
      tin,
      tae,
      fee,
      vehicleId
    } = getLoanUrlTransactionsRequest.toJSON()
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Transaction failed')
      })
      .post(`${this._config.get('API_URL')}/financing/private/${adId}/loan-request`, {
        lender,
        price,
        plateRegistrationDate,
        make,
        model,
        version,
        mileage,
        entry,
        minEntry,
        maxEntry,
        minTerms,
        maxTerms,
        terms,
        openingExpenses,
        openingPercentage,
        isOpeningExpensesFinanced,
        totalInterest,
        totalLoanCost,
        totalLoanAmount,
        totalAmountDue,
        tin,
        tae,
        fee,
        vehicleId
      })

    if (error) return Promise.reject(error)

    const {url} = response.data

    return this._urlValueObjectFactory({url})
  }

  @inlineError
  async updateTransaction({requestVO}) {
    const {adId, buyerId, sellerId, status} = requestVO.toJSON()
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Transaction failed')
      })
      .put(`${this._config.get('API_URL')}/v1/transactions`, {
        adId,
        buyerId,
        sellerId,
        status
      })

    if (error) return this._transactionErrorsFactory(error)

    return response.data.status
  }

  @inlineError
  async sendContracts(tradeEntity) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getTradeDataTransactionsErrorFactory('Transaction failed'),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .post(`${this._config.get('API_URL')}/trades/${tradeEntity.getId()}/send-contracts`)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async createLoanIdB2C({loanIdTransactionsRequest}) {
    const {adId, entry, fee, terms, withLifeInsurance, withGapInsurance} = loanIdTransactionsRequest.toJSON()
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const currentUser = await this._getCurrentUserService.execute()
    const isUserLogged = currentUser?.getToken() !== undefined
    const fetcherOptions = {
      alwaysSendAuthorizationToken: true,
      isUserLogged
    }

    const body = entry === undefined ? undefined : {entry, fee, terms, withLifeInsurance, withGapInsurance}

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Get loan id failed')
      })
      .post(
        `${this._config.get('API_URL')}/financing/professional/${adId}/loan-requests`,
        body,
        undefined,
        fetcherOptions
      )

    if (error) return Promise.reject(error)

    const {id} = response.data

    return this._loanB2CEntityFactory({loanId: id || ''})
  }

  @inlineError
  async getLoanUrlB2C({loanUrlTransactionsRequest}) {
    const {loanId} = loanUrlTransactionsRequest.toJSON()
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Get loan url failed')
      })
      .get(`${this._config.get('API_URL')}/financing/professional/loan-requests/${loanId}`)

    if (error) return Promise.reject(error)

    const {url} = response.data

    return this._loanB2CEntityFactory({loanId, loanUrl: url || ''})
  }

  @AsyncInlineError()
  async getReservationById({reservationIdVO}) {
    const reservationId = reservationIdVO.value
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Get reservation failed')
      })
      .get(`${this._config.get('API_URL')}/v2/reservations/${reservationId}`)

    if (error) return Promise.reject(error)

    return ReservationTransactionsValueObject.create({
      amount: response.data.amount,
      createdAt: response.data.createdAt,
      expirationDate: response.data.expirationDate
    })
  }

  @inlineError
  async getLoanCalculationsB2C(loanCalculationsB2CParams) {
    const {adId, entry, lender, terms, withLifeInsurance} = loanCalculationsB2CParams.toJSON()
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {NO_CONTENT} = this._config.get('COMMON').HTTP_STATUS_CODES

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._transactionErrorsFactory('Generic error in loan calculations B2C')
      })
      .post(`${this._config.get('API_URL')}/v2/financing/professional/${adId}/calculator`, {
        entry,
        terms,
        withLifeInsurance,
        lender
      })

    if (error) return Promise.reject(error)
    if (response.status === NO_CONTENT)
      return Promise.reject(this._transactionNoContentErrorFactory('No content error in loan calculations B2C'))
    return this._fromRawDataToLoanCalculationEntityObjectTransactionsMapper.map(response.data)
  }

  @inlineError
  async cancelTradeBuyer({tradeEntity}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const tradeId = tradeEntity.getId()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => this._transactionErrorsFactory(`Transaction failed ${JSON.stringify(e)}`),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .delete(`${this._config.get('API_URL')}/trades/${tradeId}?reason=buyer_trade_cancellation`)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async cancelTradeSeller({tradeEntity}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const tradeId = tradeEntity.getId()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => this._transactionErrorsFactory(`Transaction failed ${JSON.stringify(e)}`),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .delete(`${this._config.get('API_URL')}/trades/${tradeId}?reason=seller_trade_cancellation`)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async rejectTradeBuyer({tradeEntity, reason}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {REASON_REJECT_TRANSACTION_TRADE} = this._config.get('transaction')

    const hasReason = Boolean(reason)

    const tradeId = tradeEntity.getId()
    const reasonRequest = hasReason ? reason : REASON_REJECT_TRANSACTION_TRADE.BUYER_PRICE_REJECTION

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => this._transactionErrorsFactory(`Transaction failed ${JSON.stringify(e)}`),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .delete(`${this._config.get('API_URL')}/trades/${tradeId}?reason=${reasonRequest}`)

    if (error) return Promise.reject(error)
  }

  @inlineError
  async rejectTradeSeller({tradeEntity}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {GONE} = this._config.get('COMMON').HTTP_STATUS_CODES

    const tradeId = tradeEntity.getId()

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e => this._transactionErrorsFactory(`Transaction failed ${JSON.stringify(e)}`),
        [GONE]: () => AlreadyCancelledTradeTransactionsError.create('Trade already cancelled')
      })
      .delete(`${this._config.get('API_URL')}/trades/${tradeId}?reason=seller_request_rejection`)

    if (error) return Promise.reject(error)
  }

  @AsyncInlineError()
  async getAdReservationStatusNewFlow({requestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {adId, userId} = requestVO.value

    const queryString = userId ? `?${toQueryString({userId}, {encode: false})}` : ''

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: e =>
          this.TransactionError.create(`Get reservation by user failed: userId: ${userId}, error: ${JSON.stringify(e)}`)
      })
      .get(`${this._config.get('API_URL')}/v2/ads/${adId}/reservation-status${queryString}`)

    if (error) return Promise.reject(error)

    const {adReservationStatus, reservationId} = response.data

    return AdReservationStatusTransactionsValueObject.createFromApi({adId, adReservationStatus, reservationId})
  }

  @AsyncInlineError()
  async updateReservationPhoneNumber(requestVO) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {INTERNAL_SERVER_ERROR, NOT_FOUND, BAD_REQUEST, UNPROCESSABLE_CONTENT} =
      this._config.get('COMMON').HTTP_STATUS_CODES

    const reservationId = requestVO.reservationId
    const buyerPaymentPhone = requestVO.buyerPaymentPhone

    const [error] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () =>
          UpdateReservationPhoneNumberUnexpectedTransactionsError.create(
            `Unexpected error occurred while updating reservation phone number ${buyerPaymentPhone} for reservationId: ${reservationId}}`
          ),
        [NOT_FOUND]: () =>
          UpdateReservationPhoneNumberReservationNotFoundTransactionsError.create(
            `Reservation not found: ${reservationId}`
          ),
        [BAD_REQUEST]: () =>
          UpdateReservationPhoneNumberBadRequestTransactionsError.create(`Invalid phone number: ${buyerPaymentPhone}`),
        [UNPROCESSABLE_CONTENT]: () =>
          UpdateReservationPhoneNumberNotRequiredTransactionsError.create(
            `Phone number not required for reservation: ${reservationId}`
          ),
        [INTERNAL_SERVER_ERROR]: () =>
          UpdateReservationPhoneNumberUnexpectedTransactionsError.create(
            `Unexpected error occurred while updating reservation phone number ${buyerPaymentPhone} for reservationId: ${reservationId}`
          )
      })
      .patch(`${this._config.get('API_URL')}/v1/reservations/${reservationId}/phone`, {
        buyerPaymentPhone
      })

    if (error) return Promise.reject(error)
  }
}
